import React, { useCallback, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import {
  getAllAccommodationsByProject,
  getProjectByID
} from '../../services/api/implementation/impl';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';

// Validation schema
const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required('Block Name is required')
    .max(50, 'Block Name cannot exceed 50 characters'),
  area: yup
    .number()
    .typeError('Block Area must be a number')
    .required('Block Area is required')
    .positive('Block Area must be a positive number'),

  building_permission_no: yup.string().optional().nullable(),

  type: yup.string().required('Type is required'),
  number_of_levels: yup
    .number()
    .typeError('Number of Levels must be a number')
    .positive('Number of Levels must be a positive number')
    .min(1, 'Number of Levels must be at least 1')
    .required('Number of Levels is required'),

  // Conditionally require completed and date fields if type is 'Group'
  completed: yup
    .string()
    .nullable()
    .transform((value, originalValue) => {
      // Transform empty string to undefined to avoid validation issues
      return originalValue === '' || originalValue === null ? undefined : value;
    })
    .when('type', {
      is: (type) => type === 'Group',
      then: (schema) => schema.required('Status is required'),
      otherwise: (schema) => schema.notRequired().nullable()
    }),
  startDate: yup
    .date()
    .nullable()
    .transform((value, originalValue) => {
      // Transform empty string to undefined to avoid validation issues
      return originalValue === '' || originalValue === null ? undefined : value;
    })
    .when(['type', 'completed'], {
      is: (type, completed) =>
        type === 'Group' && (completed === 'Yet to Start' || completed === 'In Progress'),
      then: (schema) => schema.required('Date is required'),
      otherwise: (schema) => schema.notRequired().nullable()
    }),
  completionDate: yup
    .date()
    .nullable()
    .transform((value, originalValue) => {
      // Transform empty string to undefined to avoid validation issues
      return originalValue === '' || originalValue === null ? undefined : value;
    })
    .max(new Date(), 'Completion Date cannot be a future date')
    .when(['type', 'completed'], {
      is: (type, completed) => type === 'Group' && completed === 'Completed',
      then: (schema) => schema.required('Completion Date is required'),
      otherwise: (schema) => schema.notRequired().nullable()
    })
});

const AddNewBlock = ({
  onClose,
  onSubmit,
  onProceed,
  isSubmitting,
  buildingByProjectId,
  setIsOpenEditProject
}) => {
  const userId = Cookies.get('userId');
  /* Hooks */
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      completed: undefined,
      startDate: undefined,
      completionDate: undefined
    }
  });

  /* Query params */
  const { id: projectId } = useParams();

  const rawMeasurementUnit = Cookies.get('measurement_type');
  const measurementType =
    !rawMeasurementUnit || rawMeasurementUnit === 'null' || rawMeasurementUnit === 'undefined'
      ? 'sq/ft'
      : rawMeasurementUnit;

  const { data: accommodations } = useQuery({
    queryKey: ['accommodations', projectId],
    queryFn: () => getAllAccommodationsByProject(projectId)
  });

  const { data: projectData } = useQuery({
    queryKey: ['projectData', projectId],
    queryFn: () => getProjectByID(projectId),
    enabled: !!projectId
  });

  // Create dropdown options for building permission numbers
  const buildingPermissionOptions =
    buildingByProjectId?.map((building) => ({
      value: building.id,
      label: `${building.permissionNo}`
    })) || [];

  const selectedBlockType = watch('type', '');
  const selectedCompleted = watch('completed', '');
  const [blockTypeError, setBlockTypeError] = useState(null);

  // Set number_of_levels to 1 when Independent is selected
  // Reset completed and date fields when type changes
  useEffect(() => {
    if (selectedBlockType === 'Independent') {
      setValue('number_of_levels', 1);
      setValue('completed', null);
      setValue('startDate', null);
      setValue('completionDate', null);
    } else if (selectedBlockType !== 'Group') {
      setValue('completed', null);
      setValue('startDate', null);
      setValue('completionDate', null);
    }
  }, [selectedBlockType, setValue]);

  // Reset date fields when completed status changes
  useEffect(() => {
    if (selectedCompleted === 'Yet to Start' || selectedCompleted === 'In Progress') {
      setValue('completionDate', null);
    } else if (selectedCompleted === 'Completed') {
      setValue('startDate', null);
    } else {
      setValue('startDate', null);
      setValue('completionDate', null);
    }
  }, [selectedCompleted, setValue]);

  // Track page view
  useEffect(() => {
    mixpanel.track('Add New Block Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  // Check accommodations for selected type
  useEffect(() => {
    if (
      selectedBlockType &&
      (selectedBlockType === 'Independent' || selectedBlockType === 'Group') &&
      accommodations
    ) {
      const hasAccommodationOfType = accommodations?.some(
        (acc) => acc.accomodationType === selectedBlockType.toUpperCase()
      );

      if (!hasAccommodationOfType) {
        setBlockTypeError(`No accommodations found for ${selectedBlockType} type`);
      } else {
        setBlockTypeError(null);
      }
    } else {
      setBlockTypeError(null);
    }
  }, [selectedBlockType, accommodations]);

  // Handle form submission
  const handleProceed = (data) => {
    const payload = {
      project_id: parseInt(projectId),
      name: data.name,
      area: parseFloat(data.area),
      type: data.type,
      number_of_levels: parseInt(data.number_of_levels)
    };

    // Link to a building permission when selected (optional). Independent blocks never send building_id.
    if (data.type !== 'Independent' && data.building_permission_no) {
      const buildingId = parseInt(data.building_permission_no, 10);
      if (!Number.isNaN(buildingId)) {
        payload.building_id = buildingId;
      }
    }

    // If type is Group, include status and appropriate date field
    if (data.type === 'Group') {
      payload.status = data.completed;

      if (data.completed === 'Yet to Start' || data.completed === 'In Progress') {
        if (data.startDate) {
          payload.startDate = data.startDate;
        }
      } else if (data.completed === 'Completed' && data.completionDate) {
        payload.completionDate = data.completionDate;
      }
    }

    // console.log(payload);
    onProceed(payload);
  };

  // Limit input to numeric values  //wriet a function to restrict number of units to only number
  const handleNumberOfUnitsChange = useCallback(
    async (val) => {
      const cleanedValue = val.replace(/[^0-9]/g, '').slice(0, 8);
      setValue('number_of_levels', cleanedValue);
    },
    [setValue]
  );

  // Function to restrict block area to only numbers and decimals
  const handleBlockAreaChange = useCallback(
    async (val) => {
      // Allow numbers and a single decimal point
      const cleanedValue = val
        .replace(/[^0-9.]/g, '')
        .replace(/(\..*?)\..*/g, '$1')
        .slice(0, 8);
      setValue('area', cleanedValue);
    },
    [setValue]
  );

  return (
    <>
      <div>
        <div className="flex flex-col justify-between items-start gap-4">
          <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">Block Details</span>
        </div>
        <form onSubmit={handleSubmit(handleProceed)} className="flex flex-col gap-4 pt-6">
          <div className="flex justify-between items-center w-full">
            <FormInput
              width={48}
              label={'Project ID'}
              register={register}
              isRequired={true}
              register_key={'project_id'}
              valuePre={projectId}
              type={'hidden'}
            />
            <FormInput
              width={48}
              label={'Block Name *'}
              register={register}
              register_key={'name'}
              type={'text'}
              errors={errors.name}
            />
            <FormInput
              width={48}
              label={`Block Area (${measurementType ? measurementType : 'sq/ft'} *)`}
              register={register}
              register_key={'area'}
              type={'text'}
              onType={handleBlockAreaChange}
              errors={errors.area}
            />
          </div>

          <div className="flex justify-between items-center w-full">
            <FormInput
              width={48}
              label={'Type *'}
              register={register}
              register_key={'type'}
              type={'dropdown'}
              options={[
                { value: '', label: 'Select Type', disabled: true, selected: true },
                { value: 'Independent', label: 'Independent' },
                { value: 'Group', label: 'Group' }
              ]}
              errors={errors.type}
            />
            <div className="w-[48%] flex gap-2 items-end">
              <div className="flex-1">
                <FormInput
                  width={100}
                  label="Building Permission No (Optional)"
                  register={register}
                  register_key={'building_permission_no'}
                  type={'dropdown'}
                  options={[
                    {
                      value: '',
                      label: 'Select Building Permission No',
                      disabled: true,
                      selected: true
                    },
                    ...buildingPermissionOptions
                  ]}
                  errors={errors.building_permission_no}
                />
              </div>
              <Button
                type="button"
                onClick={() => setIsOpenEditProject(true)}
                displayType="secondary"
                className="mb-1 px-3 py-2 text-sm h-10">
                Add
              </Button>
            </div>
          </div>

          <div className="flex justify-between items-center w-full">
            {selectedBlockType !== 'Independent' && (
              <FormInput
                width={48}
                label={'Number of Levels *'}
                register={register}
                register_key={'number_of_levels'}
                type={'text'}
                onType={handleNumberOfUnitsChange}
                errors={errors.number_of_levels}
              />
            )}

            {selectedBlockType === 'Group' && (
              <FormInput
                width={48}
                label={'Status *'}
                register={register}
                register_key={'completed'}
                type={'dropdown'}
                options={[
                  { value: '', label: 'Select Status', disabled: true, selected: true },
                  { value: 'Yet to Start', label: 'Yet to Start' },
                  { value: 'In Progress', label: 'In Progress' },
                  { value: 'Completed', label: 'Completed' }
                ]}
                errors={errors.completed}
              />
            )}
          </div>

          {/* Show Expected Start Date field when status is "Yet to Start" */}
          {selectedBlockType === 'Group' && selectedCompleted === 'Yet to Start' && (
            <div className="flex justify-between items-center w-full">
              <FormInput
                width={48}
                label={'Expected Start Date *'}
                register={register}
                register_key={'startDate'}
                type={'date'}
                errors={errors.startDate}
              />
            </div>
          )}

          {/* Show Actual Start Date field when status is "In Progress" */}
          {selectedBlockType === 'Group' && selectedCompleted === 'In Progress' && (
            <div className="flex justify-between items-center w-full">
              <FormInput
                width={48}
                label={'Actual Start Date *'}
                register={register}
                register_key={'startDate'}
                type={'date'}
                errors={errors.startDate}
                CustomFeature={{
                  max: new Date().toISOString().split('T')[0]
                }}
              />
            </div>
          )}

          {/* Show Completion Date field when status is "Completed" */}
          {selectedBlockType === 'Group' && selectedCompleted === 'Completed' && (
            <div className="flex justify-between items-center w-full">
              <FormInput
                width={48}
                label={'Completion Date *'}
                register={register}
                register_key={'completionDate'}
                type={'date'}
                errors={errors.completionDate}
                CustomFeature={{
                  max: new Date().toISOString().split('T')[0]
                }}
              />
            </div>
          )}

          {blockTypeError && <div className="text-red-500 mt-2">{blockTypeError}</div>}

          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button type="reset" onClick={onClose} displayType="secondary">
              Cancel
            </Button>
            <Button type="submit" disabled={blockTypeError || isSubmitting}>
              Proceed
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddNewBlock;
